import React, { useState, useEffect, useRef } from 'react';

export default function Accounts({ enrollments, userRole }) {
  // Use local state so the UI updates immediately after a status change
  const [localData, setLocalData] = useState([]);
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // --- NEW: Status Filter States ---
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const filterRef = useRef(null); // Used to detect clicks outside the dropdown

  // Sync prop data to local state initially and on changes
  useEffect(() => {
    setLocalData(enrollments || []);
  }, [enrollments]);

  // Close the status dropdown if the user clicks outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsStatusFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- DB UPDATE FUNCTION ---
  const handleStatusUpdate = async (id, newStatus) => {
    try {
      const apiHeaders = {
        'Content-Type': 'application/json'
      };
      const apiPayload = { Status: newStatus }
      const response = await fetch(`https://tsaapis.operate.live/api/payments/${id}`, {
        method: 'PUT',
        headers: apiHeaders,
        credentials: 'include',
        body: JSON.stringify(apiPayload)
      });

      // Optimistic UI Update: Update the record in the local state
      setLocalData(prevData =>
        prevData.map(item =>
          item.paymentId === id ? { ...item, Status: newStatus } : item
        )
      );

    } catch (error) {
      console.error("Failed to update status in DB:", error);
      alert("Failed to update the status. Please try again.");
    }
  };

  // Extract unique statuses dynamically from the data for the filter menu
  const uniqueStatuses = Array.from(new Set(localData.map(item => item.Status).filter(Boolean)));

  // Apply filters to localData instead of raw props
  const filteredData = localData.filter(item => {
    if(!item.id) return false;
    
    const matchSearch =
      item.name.toLowerCase().includes(search.toLowerCase()) ||
      item.regId.toLowerCase().includes(search.toLowerCase()) ||
      item.transactionRef.toLowerCase().includes(search.toLowerCase()) ||
      item.title.toLowerCase().includes(search.toLowerCase());

    let matchStartDate = true;
    let matchEndDate = true;

    if (startDate) matchStartDate = new Date(item.paidDate) >= new Date(startDate);
    if (endDate) matchEndDate = new Date(item.paidDate) <= new Date(endDate);

    // --- NEW: Check against selected statuses ---
    // If the array is empty, it means "Show All"
    const matchStatus = selectedStatuses.length === 0 || selectedStatuses.includes(item.Status);

    return matchSearch && matchStartDate && matchEndDate && matchStatus;
  });

  // Calculate Cumulative Total
  const totalAmount = filteredData.reduce((sum, item) => sum + item.paidAmount, 0);

  // --- EXPORT TO EXCEL (CSV) FUNCTION ---
  const exportToExcel = () => {
    const headers = ['Student ID', 'Name', 'Batch Title', 'Paid Date', 'Transaction No.', 'Amount Paid', 'Status'];

    const csvRows = filteredData.map(row => {
      return [
        row.regId,
        `"${row.name}"`,
        `"${row.title}"`,
        new Date(row.paidDate).toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        }),
        `"${row.transactionRef}"`,
        `"${row.Status}"`, // Included status in export
        row.paidAmount
      ].join(',');
    });

    csvRows.push(`,,,,,,"Cumulative Total:",${totalAmount}`);
    const csvString = [headers.join(','), ...csvRows].join('\n');

    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // --- DYNAMIC FILENAME GENERATOR ---
    let filenameParts = ['Accounts_Ledger'];

    if (startDate || endDate) {
      filenameParts.push(`${startDate || 'Any'}_to_${endDate || 'Any'}`);
    }

    if (search) {
      const cleanSearch = search.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 15);
      filenameParts.push(`Search_${cleanSearch}`);
    }

    if (!startDate && !endDate && !search) {
      const today = new Date().toISOString().split('T')[0];
      filenameParts.push(today);
    }

    const finalFilename = `${filenameParts.join('_')}.csv`;
    link.setAttribute('download', finalFilename);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="admin-card" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* HEADER WITH EXPORT BUTTON */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
        <h2 style={{ width: "100%" }}>
          <div>Accounts & Revenue Ledger</div>
          <div>
            <button className="submit-btn" style={{ margin: 0, padding: '8px 15px', backgroundColor: '#10b981', color: 'white', border: 'none', cursor: 'pointer', borderRadius: '4px' }} onClick={exportToExcel}>
              <i className="fas fa-file-excel"></i> Export to Excel
            </button>
          </div>
        </h2>
      </div>

      {/* FILTERS */}
      <div className="filter-toolbar" style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginTop: '15px', marginBottom: '15px' }}>
        <input
          type="text"
          className="filter-input"
          placeholder="Search student, ID, batch, or tx ref..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: '250px', padding: '8px', borderRadius: '4px' }}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ color: 'var(--text-gray)' }}>From:</span>
          <input type="date" className="filter-input" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ padding: '8px', borderRadius: '4px' }} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ color: 'var(--text-gray)' }}>To:</span>
          <input type="date" className="filter-input" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ padding: '8px', borderRadius: '4px' }} />
        </div>
      </div>

      {/* DATA GRID */}
      <div className="table-responsive" style={{ flex: 1, overflowY: 'auto', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '8px' }}>
        <table style={{ margin: 0, width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <th style={{ padding: '12px' }}>Student ID</th>
              <th style={{ padding: '12px' }}>Name</th>
              <th style={{ padding: '12px' }}>Batch Title</th>
              <th style={{ padding: '12px' }}>Paid Date</th>
              <th style={{ padding: '12px' }}>Transaction No.</th>
              <th style={{ textAlign: 'right', padding: '12px' }}>Amount Paid</th>
              
              {/* UPDATED: STATUS HEADER WITH FILTER MENU */}
              <th style={{ padding: '12px', textAlign: 'center', position: 'relative', zIndex: 20 }} ref={filterRef}>
                <div 
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => setIsStatusFilterOpen(!isStatusFilterOpen)}
                >
                  Status 
                  <i 
                    className="fas fa-filter" 
                    style={{ 
                      fontSize: '0.8rem', 
                      color: selectedStatuses.length > 0 ? '#10b981' : 'var(--text-gray)' 
                    }}
                  ></i>
                </div>

                {isStatusFilterOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', right: '50%', transform: 'translateX(50%)',
                    backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '6px',
                    padding: '12px', zIndex: 50, minWidth: '160px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.5)',
                    textAlign: 'left'
                  }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '10px', color: 'white', borderBottom: '1px solid #334155', paddingBottom: '6px' }}>
                      Filter Status
                    </div>
                    
                    {uniqueStatuses.map(status => (
                      <label key={status} style={{ display: 'flex', alignItems: 'left', margin: '8px 0', cursor: 'pointer', fontSize: '0.9rem', color: '#cbd5e1' }}>
                        <input
                          type="checkbox"
                          checked={selectedStatuses.includes(status)}
                          onChange={() => {
                            setSelectedStatuses(prev =>
                              prev.includes(status) 
                                ? prev.filter(s => s !== status) 
                                : [...prev, status]
                            );
                          }}
                          style={{ marginRight: '8px', cursor: 'pointer', width: 'auto' }}
                        />
                        {status}
                      </label>
                    ))}
                    
                    {uniqueStatuses.length === 0 && (
                      <div style={{ fontSize: '0.8rem', color: '#64748b', textAlign: 'center' }}>No statuses</div>
                    )}
                    
                    {selectedStatuses.length > 0 && (
                      <div
                        style={{ marginTop: '12px', color: '#ef4444', fontSize: '0.85rem', cursor: 'pointer', textAlign: 'left', fontWeight: 'bold' }}
                        onClick={() => setSelectedStatuses([])}
                      >
                        Clear Filter
                      </div>
                    )}
                  </div>
                )}
              </th> 
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr><td colSpan="7" style={{ textAlign: 'center', padding: '3rem' }}>No transaction records found for the selected filters.</td></tr>
            ) : (
              filteredData.map(row => (
                <tr key={row.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <td style={{ color: 'var(--accent-gold)', fontWeight: 'bold', padding: '12px' }}>{row.regId}</td>
                  <td style={{ padding: '12px' }}><strong>{row.name}</strong></td>
                  <td style={{ padding: '12px' }}>{row.title}</td>
                  <td style={{ padding: '12px' }}>
                    <i className="far fa-calendar-alt" style={{ color: 'var(--text-gray)', marginRight: '5px' }}></i>
                    {new Date(row.paidDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span className="badge" style={{ background: 'rgba(255,255,255,0.1)', color: '#cbd5e1', padding: '4px 8px', borderRadius: '4px' }}>
                      {row.transactionRef}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#10b981', fontSize: '1.1rem', padding: '12px' }}>
                    ₹{row.paidAmount.toLocaleString('en-IN')}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>

                    {/* CONDITIONAL STATUS BUTTONS */}
                    {row.Status !== 'Confirmed' && row.Status !== 'Failed' && userRole === 'admin' ? (
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          onClick={() => handleStatusUpdate(row.paymentId, 'Confirmed')}
                          style={{ backgroundColor: '#10b981', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
                          title="Mark as Confirmed"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => handleStatusUpdate(row.paymentId, 'Failed')}
                          style={{ backgroundColor: '#ef4444', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
                          title="Mark as Failed"
                        >
                          Fail
                        </button>
                      </div>
                    ) : (
                      <span className="badge" style={{
                        background: row.Status === 'Confirmed' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                        color: row.Status === 'Confirmed' ? '#10b981' : '#ef4444',
                        padding: '6px 12px',
                        borderRadius: '4px',
                        fontWeight: 'bold'
                      }}>
                        {row.Status}
                      </span>
                    )}

                  </td>
                </tr>
              ))
            )}
          </tbody>

          {/* CUMULATIVE TOTAL FOOTER */}
          <tfoot style={{ position: 'sticky', bottom: 0, backgroundColor: 'var(--primary-dark)', boxShadow: '0 -4px 15px rgba(0,0,0,0.4)', zIndex: 10 }}>
            <tr>
              <td colSpan="5" style={{ textAlign: 'right', fontWeight: 'bold', fontSize: '1.1rem', padding: '15px', textTransform: 'uppercase', color: 'var(--text-gray)' }}>
                Cumulative Total:
              </td>
              <td style={{ textAlign: 'right', fontWeight: 'bold', fontSize: '1.4rem', color: 'var(--accent-gold)', padding: '15px' }}>
                ₹{totalAmount.toLocaleString('en-IN')}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}